"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateClient = generateClient;
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
const core_1 = require("@aws-amplify/core");
const utils_1 = require("@aws-amplify/core/internals/utils");
const runtime_1 = require("@aws-amplify/data-schema/runtime");
const types_1 = require("../types");
const isApiGraphQLProviderConfig_1 = require("./utils/runtimeTypeGuards/isApiGraphQLProviderConfig");
const isConfigureEventWithResourceConfig_1 = require("./utils/runtimeTypeGuards/isConfigureEventWithResourceConfig");
const v6_1 = require("./v6");
/**
 * @private
 *
 * Creates a client that can be used to make GraphQL requests, using a provided `AmplifyClassV6`
 * compatible context object for config and auth fetching.
 *
 * @param params
 * @returns
 */
function generateClient(params) {
    // Bridge AmplifyClassV6 to AmplifyContext if needed
    const amplifyCtx = (0, core_1.isAmplifyContext)(params.amplify)
        ? params.amplify
        : (0, utils_1.bridgeAmplifyClass)(params.amplify);
    const client = {
        [types_1.__amplify]: amplifyCtx,
        [types_1.__authMode]: params.authMode,
        [types_1.__authToken]: params.authToken,
        [types_1.__apiKey]: 'apiKey' in params ? params.apiKey : undefined,
        [types_1.__endpoint]: 'endpoint' in params ? params.endpoint : undefined,
        [types_1.__headers]: params.headers,
        graphql: v6_1.graphql,
        cancel: v6_1.cancel,
        isCancelError: v6_1.isCancelError,
        models: emptyProperty,
        enums: emptyProperty,
        queries: emptyProperty,
        mutations: emptyProperty,
        subscriptions: emptyProperty,
    };
    const apiGraphqlConfig = amplifyCtx.resourcesConfig.API?.GraphQL;
    if (client[types_1.__endpoint]) {
        if (!client[types_1.__authMode]) {
            throw new Error('generateClient() requires an explicit `authMode` when `endpoint` is provided.');
        }
        if (client[types_1.__authMode] === 'apiKey' && !client[types_1.__apiKey]) {
            throw new Error("generateClient() requires an explicit `apiKey` when `endpoint` is provided and `authMode = 'apiKey'`.");
        }
    }
    if (!client[types_1.__endpoint]) {
        if ((0, isApiGraphQLProviderConfig_1.isApiGraphQLConfig)(apiGraphqlConfig)) {
            (0, runtime_1.addSchemaToClient)(client, apiGraphqlConfig, types_1.getInternals);
        }
        else {
            // This happens when the `Amplify.configure()` call gets evaluated after the `generateClient()` call.
            //
            // Cause: when the `generateClient()` and the `Amplify.configure()` calls are located in
            // different source files, script bundlers may randomly arrange their orders in the production
            // bundle.
            //
            // With the current implementation, the `client.models` instance created by `generateClient()`
            // will be rebuilt on every `Amplify.configure()` call that's provided with a valid GraphQL
            // provider configuration.
            //
            // TODO: revisit, and reverify this approach when enabling multiple clients for multi-endpoints
            // configuration.
            generateModelsPropertyOnAmplifyConfigure(client);
        }
    }
    return client;
}
const generateModelsPropertyOnAmplifyConfigure = (clientRef) => {
    core_1.Hub.listen('core', coreEvent => {
        if (!(0, isConfigureEventWithResourceConfig_1.isConfigureEventWithResourceConfig)(coreEvent.payload)) {
            return;
        }
        const apiGraphQLConfig = coreEvent.payload.data.API?.GraphQL;
        if ((0, isApiGraphQLProviderConfig_1.isApiGraphQLConfig)(apiGraphQLConfig)) {
            (0, runtime_1.addSchemaToClient)(clientRef, apiGraphQLConfig, types_1.getInternals);
        }
    });
};
const emptyProperty = new Proxy({}, {
    get() {
        throw new Error('Client could not be generated. This is likely due to `Amplify.configure()` not being called prior to `generateClient()` or because the configuration passed to `Amplify.configure()` is missing GraphQL provider configuration.');
    },
});
